import React from "react";
import { ExternalLink } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { INK, PRIMARY, Overline, AuroraBlue, AuroraLight, Reveal } from "../components/shared/kursusUi";

const UsefulLinksPage = () => {
  const linkCategories = [
    {
      title: "Offentlige myndigheder",
      items: [
        { name: "Borger.dk", url: "https://www.borger.dk", description: "Danmarks officielle selvbetjeningsportal" },
        { name: "Færdselsstyrelsen", url: "https://www.fstyr.dk", description: "Ansøg om sprogdispensation" },
        { name: "Rigsarkivet", url: "https://www.rigsarkivet.dk", description: "Fremskaffe tidligere sprogkrav dokumentation" },
        { name: "Jobnet.dk", url: "https://www.jobnet.dk", description: "Jobsøgning og ledighed" }
      ]
    },
    {
      title: "Uddannelse & Tilskud",
      items: [
        { name: "Voksenuddannelse.dk (AUB)", url: "https://www.voksenuddannelse.dk", description: "Søg VEU-godtgørelse" },
        { name: "Min Kompetencemappe", url: "https://www.minkompetencemappe.dk", description: "Find gamle kursusbeviser" },
        { name: "AMU-kurser.dk", url: "https://www.amu-kurser.dk", description: "Find AMU-kurser" },
        { name: "UddannelsesGuiden", url: "https://www.ug.dk", description: "Information om uddannelser" },
        { name: "STAR", url: "https://www.star.dk", description: "Styrelsen for Arbejdsmarked og Rekruttering" }
      ]
    },
    {
      title: "A-kasser",
      items: [
        { name: "3F", url: "https://www.3f.dk", description: "A-kasse for transport, service og fødevarer" },
        { name: "HK", url: "https://www.hk.dk", description: "A-kasse for handel og kontor" },
        { name: "Kristelig A-kasse", url: "https://www.krifa.dk", description: "Tværfaglig A-kasse" },
        { name: "FTF-A", url: "https://www.ftfa.dk", description: "A-kasse for funktionærer" },
        { name: "ASE", url: "https://www.ase.dk", description: "A-kasse for selvstændige" },
        { name: "Akademikernes A-kasse", url: "https://www.akademikerne.dk", description: "A-kasse for akademikere" },
        { name: "DANA", url: "https://www.dana.dk", description: "Det Faglige Hus A-kasse" },
        { name: "CA", url: "https://www.ca.dk", description: "C3 A-kasse (tidl. CA)" },
        { name: "Min A-kasse", url: "https://www.min-a-kasse.dk", description: "Tværfaglig A-kasse" },
        { name: "Ledernes A-kasse", url: "https://www.lederne.dk", description: "A-kasse for ledere" },
        { name: "FOA", url: "https://www.foa.dk", description: "A-kasse for offentligt ansatte" },
        { name: "Magistrenes A-kasse", url: "https://www.ma-kasse.dk", description: "A-kasse for magistre" },
        { name: "Byggefagenes A-kasse", url: "https://www.bygga.dk", description: "A-kasse for byggebranchen" },
        { name: "Metal A-kasse", url: "https://www.danskmetal.dk", description: "A-kasse for metalarbejdere" },
        { name: "NNF's A-kasse", url: "https://www.nnf.dk", description: "A-kasse for fødevarebranchen" },
        { name: "El-fagets A-kasse", url: "https://www.LO.dk/LO/elfaget", description: "A-kasse for elektrikere" },
        { name: "Frie Funktionærers A-kasse", url: "https://www.LO.dk", description: "Frie Funktionærer" }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 20%, 98%)' }}>
      <div className="votra-grain" aria-hidden />
      <Header />

      {/* Hero — dark ink + blue aurora */}
      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: '#F4F6FB' }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-16 md:py-24 text-center">
          <Reveal className="flex justify-center"><Overline light>Ressourcer</Overline></Reveal>
          <Reveal i={1}>
            <h1 className="font-display text-4xl md:text-5xl font-medium text-white leading-tight mb-5">Nyttige links</h1>
          </Reveal>
          <Reveal i={2}>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Her finder du en samling af nyttige links relateret til uddannelse, jobsøgning, A-kasser og offentlige myndigheder.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-16 md:py-20 flex-1">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {linkCategories.map((category, idx) => (
              <Reveal i={idx} key={idx} className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-[0_8px_30px_rgba(15,40,90,0.06)]">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/70">
                  <h3 className="font-display text-lg font-medium text-slate-900">
                    {category.title}
                  </h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {category.items.map((link, linkIdx) => (
                    <a
                      key={linkIdx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div>
                        <p className="font-medium" style={{ color: PRIMARY }}>{link.name}</p>
                        <p className="text-sm text-slate-600">{link.description}</p>
                      </div>
                      <ExternalLink className="h-5 w-5 text-slate-400 flex-shrink-0 ml-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UsefulLinksPage;
